import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  alterarClientes,
  corrigirAssunto,
  definirDadosDeProduto,
  definirPrazo,
  definirPrioridade,
  definirResponsavel,
  definirWorkItem,
  editarOrigem,
  listarAcoes,
  obterAcao,
  obterEstadosDeWorkItem,
  obterResumo,
  transicionar,
  type DadosDeProduto,
  type EdicaoDeOrigem,
  type FiltroDeAcoes,
} from '@/features/acoes/api/acoesApi';
import type {
  AcaoDetalhada,
  EstadosDeWorkItem,
  PaginaDeAcoes,
  PrioridadeAcao,
  ResumoDeAcoes,
  TipoArea,
  TipoTransicao,
} from '@/types/api';

/**
 * Chaves de cache das acoes.
 *
 * O filtro entra na chave da lista: paginas e filtros diferentes sao respostas
 * diferentes, e compartilhar chave faria a tela mostrar o resultado do filtro
 * anterior enquanto o novo carrega.
 */
export const chavesDeAcoes = {
  todas: ['acoes'] as const,
  lista: (filtro: FiltroDeAcoes) => ['acoes', 'lista', filtro] as const,
  item: (id: number) => ['acoes', 'item', id] as const,

  // O recorte entra na chave: o resumo do Painel CS e do Painel Produto sao respostas
  // diferentes, e compartilhar chave faria um mostrar os numeros do outro ao trocar de aba.
  resumo: (tipos: TipoArea[], clienteIds: number[]) =>
    ['acoes', 'resumo', tipos, clienteIds] as const,
};

/**
 * Contagens do painel.
 *
 * Sob a mesma raiz `acoes` de proposito: criar uma acao ou aplicar uma transicao muda as
 * contagens, e as invalidacoes que ja existem passam a alcancar o painel sem que cada
 * ponto de escrita precise lembrar dele.
 */
export function useResumoDeAcoes(
  tiposDeAreaResponsavel: TipoArea[],
  clienteIds: number[],
): UseQueryResult<ResumoDeAcoes> {
  return useQuery({
    queryKey: chavesDeAcoes.resumo(tiposDeAreaResponsavel, clienteIds),
    queryFn: ({ signal }) => obterResumo(tiposDeAreaResponsavel, clienteIds, signal),
  });
}

export function useListaDeAcoes(filtro: FiltroDeAcoes): UseQueryResult<PaginaDeAcoes> {
  return useQuery({
    queryKey: chavesDeAcoes.lista(filtro),
    queryFn: ({ signal }) => listarAcoes(filtro, signal),

    /**
     * Mantem a pagina anterior visivel durante a troca de pagina ou de filtro.
     * Sem isso a tabela desaparece e reaparece, e a pagina salta sob o cursor.
     */
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Estado dos work items da pagina, no Azure DevOps.
 *
 * FORA DA ARVORE `acoes`, de proposito. As invalidacoes de escrita de acao derrubam
 * `chavesDeAcoes.todas`, e o estado no Azure nao muda porque alguem concluiu uma acao aqui —
 * refazer a consulta ao Azure a cada transicao gastaria chamada sem ganho.
 *
 * `enabled` desliga a consulta quando a pagina nao tem nenhuma task: sem isso, um painel sem
 * work item nenhum ainda bateria no Azure a cada render.
 */
export function useEstadosDeWorkItem(ids: number[]): UseQueryResult<EstadosDeWorkItem> {
  // Ordenado para a chave nao mudar so porque a ordenacao da tabela mudou: a resposta e a
  // mesma, e uma chave diferente refaria a chamada.
  const chave = [...ids].sort((a, b) => a - b);

  return useQuery({
    queryKey: ['work-items', chave],
    queryFn: ({ signal }) => obterEstadosDeWorkItem(chave, signal),
    enabled: chave.length > 0,

    // Estado de task muda devagar, e a tabela e reconsultada a cada troca de pagina e de
    // filtro. Cinco minutos evita repetir a chamada ao voltar para uma pagina ja vista.
    staleTime: 5 * 60 * 1000,
  });
}

export function useAcao(id: number): UseQueryResult<AcaoDetalhada> {
  return useQuery({
    queryKey: chavesDeAcoes.item(id),
    queryFn: ({ signal }) => obterAcao(id, signal),
  });
}

interface VariaveisDaTransicao {
  transicao: TipoTransicao;
  justificativa?: string;
}

/**
 * Aplica uma transicao e atualiza o cache.
 *
 * SEM ATUALIZACAO OTIMISTA. A transicao pode ser recusada pela maquina de estados
 * com 409, e antecipar o novo estado na tela mostraria uma situacao que o servidor
 * nao aceitou — para depois voltar atras. Numa tela de workflow isso e pior do que
 * esperar a resposta: o usuario tomaria a proxima decisao com base no estado
 * errado.
 *
 * A resposta do servidor ja e a acao detalhada e inteira, e vai direto para o
 * cache. Uma consulta a mais depois de cada transicao seria desperdicio.
 */
export function useTransicao(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, VariaveisDaTransicao> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transicao, justificativa }: VariaveisDaTransicao) =>
      transicionar(id, transicao, justificativa),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);

      // A lista tem situacao, saude de prazo e data de atualizacao, e todas as
      // tres mudam com a transicao. Invalidar a arvore de listas e mais simples
      // do que localizar a linha em cada pagina em cache.
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

/**
 * Edita o conteudo de origem.
 *
 * SEM ATUALIZACAO OTIMISTA, pelo mesmo motivo de `useTransicao` e `useDefinirWorkItem`: o
 * grupo Origem fecha fora de Rascunho e AjusteSolicitado, e antecipar o texto editado na
 * tela mostraria uma edicao que o servidor pode recusar com 409.
 *
 * A lista nao e invalidada: ela mostra assunto, e o unico outro campo deste grupo que
 * aparece lá e a area solicitante, que tambem muda aqui. `invalidateQueries` cobre os
 * dois sem precisar escolher.
 */
export function useEditarOrigem(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, EdicaoDeOrigem> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (edicao: EdicaoDeOrigem) => editarOrigem(id, edicao),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

/**
 * Substitui a lista de clientes afetados.
 *
 * Mutation separada de `useEditarOrigem`, espelhando as duas rotas do servidor: o
 * dominio trata clientes como sua propria operacao, com a regra de "ao menos um
 * cliente" validada ali, nao em `EditarOrigemAsync`.
 */
export function useAlterarClientes(id: number): UseMutationResult<AcaoDetalhada, Error, number[]> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clienteIds: number[]) => alterarClientes(id, clienteIds),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

interface VariaveisDoResponsavel {
  responsavelId: number | null;
  motivo?: string;
}

/**
 * Define ou remove o responsavel.
 *
 * SEM ATUALIZACAO OTIMISTA, mesmo motivo de `useDefinirWorkItem`: o grupo Processo pode
 * estar fechado (rascunho, terminais), e antecipar o responsavel na tela mostraria um
 * vinculo que o servidor nao aceitou.
 */
export function useDefinirResponsavel(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, VariaveisDoResponsavel> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ responsavelId, motivo }: VariaveisDoResponsavel) =>
      definirResponsavel(id, responsavelId, motivo),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

/** Define ou remove a prioridade. */
export function useDefinirPrioridade(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, PrioridadeAcao | null> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prioridade: PrioridadeAcao | null) => definirPrioridade(id, prioridade),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

interface VariaveisDoPrazo {
  prazo: string | null;
  motivo?: string;
}

/**
 * Define ou repactua o prazo.
 *
 * A LISTA E INVALIDADA: a coluna Prazo e a saude do prazo mudam na tabela de fora, igual
 * a `useTransicao`.
 */
export function useDefinirPrazo(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, VariaveisDoPrazo> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ prazo, motivo }: VariaveisDoPrazo) => definirPrazo(id, prazo, motivo),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

/**
 * Define PO, Dev, Qa, Complexidade, Financiada e Encaixe.
 *
 * Exclusivo de area responsavel do tipo Produto — o servidor recusa com 409 nas demais,
 * mas a tela so chama este hook quando `acao.tipoAreaResponsavel === 'Produto'`.
 */
export function useDefinirDadosDeProduto(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, DadosDeProduto> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dados: DadosDeProduto) => definirDadosDeProduto(id, dados),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}

interface VariaveisDoWorkItem {
  /** Numero da task. `null` desvincula. */
  workItem: number | null;
  motivo?: string;
}

/**
 * Vincula, troca ou desvincula a task do Azure DevOps.
 *
 * SEM ATUALIZACAO OTIMISTA, pelo mesmo motivo de `useTransicao`: o dominio recusa com 409
 * quando o grupo Processo esta fechado — rascunho e as terminais —, e antecipar o numero na
 * tela mostraria um vinculo que o servidor nao aceitou.
 *
 * A resposta ja e a acao inteira e vai direto para o cache. Depois disso a arvore de listas
 * e invalidada, porque a coluna Task da tabela mudou.
 */
export function useDefinirWorkItem(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, VariaveisDoWorkItem> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workItem, motivo }: VariaveisDoWorkItem) =>
      definirWorkItem(id, workItem, motivo),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);

      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });

      /*
        O ESTADO DA TASK TAMBEM PRECISA SER REBUSCADO.

        A consulta de estado vive fora da arvore `acoes`, de proposito — estado no Azure nao
        muda porque alguem transicionou aqui. Mas vincular uma task NOVA e o caso em que ela
        precisa ser consultada: sem isto, a coluna ficaria sem selo ate o cache de 5 minutos
        expirar, e pareceria que o Azure nao respondeu.
      */
      void queryClient.invalidateQueries({ queryKey: ['work-items'] });
    },
  });
}

interface VariaveisDaCorrecaoDeAssunto {
  assunto: string;
  motivo: string;
}

/**
 * Corrige o Assunto fora do grupo Origem.
 *
 * A LISTA E INVALIDADA: a coluna Assunto da tabela de fora muda, igual a `useTransicao`.
 */
export function useCorrigirAssunto(
  id: number,
): UseMutationResult<AcaoDetalhada, Error, VariaveisDaCorrecaoDeAssunto> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assunto, motivo }: VariaveisDaCorrecaoDeAssunto) =>
      corrigirAssunto(id, assunto, motivo),

    onSuccess: (acao) => {
      queryClient.setQueryData(chavesDeAcoes.item(id), acao);
      void queryClient.invalidateQueries({ queryKey: chavesDeAcoes.todas });
    },
  });
}
